import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AddCircleOutline,
  ArticleOutlined,
  BrushOutlined,
  CheckCircleOutline,
  CloudOffOutlined,
  ErrorOutline,
  FactCheckOutlined,
  GavelOutlined,
  RemoveCircleOutline,
  ReportProblemOutlined,
  SyncProblemOutlined,
} from '@mui/icons-material';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { fetchData, postData } from '../../helpers/apiUtils';
import {
  Discrepancy,
  FullProfile,
  HubBroker,
  MatrixData,
  ROLLOUT_TEXTE,
  RolloutZustand,
  STATUS_STYLES,
} from './types';
import { HelpLegend, MATRIX_HELP } from './help';
import NewComparePageDialog from './NewComparePageDialog';
import ProfileEditorDialog from './ProfileEditorDialog';
import NewProfileDialog from './NewProfileDialog';
import ComplianceDialog from './ComplianceDialog';
import FactsDialog from './FactsDialog';
import DesignDialog from './DesignDialog';
import OverviewTextDialog from './OverviewTextDialog';
import BrokerEditDialog from './BrokerEditDialog';
import BrokerRolloutDialog from './BrokerRolloutDialog';
import AffiliateLinksDialog from './AffiliateLinksDialog';
import SnippetsTab from './snippets/SnippetsTab';
import BrokerCloudTab from './cloud/BrokerCloudTab';
import AdsLiveTab from './ads/AdsLiveTab';

/**
 * Broker Hub — centralized broker profile management for the 6 publisher portals.
 * Matrix (broker × site) + discrepancy worklist; opens the fullscreen editor,
 * the new-profile dialog, and the central Design (CSS) dialog.
 */
const BrokerHub: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tab, setTab] = useState(0);
  const [matrix, setMatrix] = useState<MatrixData | null>(null);
  // Rollout-Zustand je Broker. Er steht in der Datenbank, nicht in dieser Sitzung — deshalb
  // gehört er in die Matrix und nicht nur in den Dialog: eine Unvollständigkeit, die man nur
  // sieht, wenn man den Dialog öffnet, sieht niemand.
  const [rollout, setRollout] = useState<Record<number, RolloutZustand>>({});
  const [rolloutFuer, setRolloutFuer] = useState<HubBroker | null>(null);
  // Affiliate-Links je Broker — aus der Matrix-Zeile UND aus dem Profil-Editor geöffnet.
  const [affiliateFuer, setAffiliateFuer] = useState<{ id: number; name: string } | null>(null);
  // Hochgezählt, wenn der Dialog etwas gespeichert/ausgespielt hat — der offene Editor lädt dann neu.
  const [affiliateStand, setAffiliateStand] = useState(0);
  const [bearbeiteBroker, setBearbeiteBroker] = useState<HubBroker | null>(null);
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editorProfileId, setEditorProfileId] = useState<number | null>(null);
  const [newProfileOpen, setNewProfileOpen] = useState(false);
  const [comparePages, setComparePages] = useState<any[]>([]);
  const [newComparePageOpen, setNewComparePageOpen] = useState(false);
  const [designOpen, setDesignOpen] = useState(false);
  const [overviewTextOpen, setOverviewTextOpen] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [factsOpen, setFactsOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const [matrixRes, discRes, pagesRes, rolloutRes] = await Promise.all([
      fetchData<MatrixData>('/api/broker-hub/matrix'),
      fetchData<Discrepancy[]>('/api/broker-hub/discrepancies?status=open'),
      fetchData<any[]>('/api/broker-hub/pages'),
      fetchData<Record<number, RolloutZustand>>('/api/broker-hub/brokers/rollout'),
    ]);
    if (matrixRes.success && matrixRes.data) setMatrix(matrixRes.data);
    else setError(matrixRes.error || 'Matrix konnte nicht geladen werden');
    if (discRes.success && discRes.data) setDiscrepancies(discRes.data);
    // Fehlschlag hier setzt bewusst KEINEN Seitenfehler: die Matrix ist die Hauptansicht und soll
    // nicht dunkel bleiben, weil der Vergleichsseiten-Reiter nicht laden konnte.
    if (pagesRes.success && Array.isArray(pagesRes.data)) setComparePages(pagesRes.data);
    // Wie oben: ein Fehlschlag hier darf die Matrix nicht verdunkeln. Ohne Zustand zeigt die
    // Spalte nichts an — sie behauptet dann aber auch NICHT „vollständig".
    if (rolloutRes.success && rolloutRes.data) setRollout(rolloutRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolveDiscrepancy = async (id: number, status: 'resolved' | 'dismissed') => {
    const res = await postData(`/api/broker-hub/discrepancies/${id}/resolve`, { status });
    if (res.success) setDiscrepancies((prev) => prev.filter((d) => d.id !== id));
  };

  const onProfileCreated = (profile: FullProfile) => {
    setNewProfileOpen(false);
    load();
    setEditorProfileId(profile.id);
  };

  const renderCell = (broker: HubBroker, siteKey: string) => {
    const cell = broker.profiles[siteKey];
    if (!cell) {
      return (
        <Tooltip title={'Kein Profil — Klick auf „Neues Profil" zum Anlegen'}>
          <RemoveCircleOutline sx={{ color: colors.grey[600], fontSize: 18 }} />
        </Tooltip>
      );
    }
    const style = STATUS_STYLES[cell.status] ?? { label: cell.status, color: 'default' as const };
    // Deploy-state marker: the stored status is what the hub INTENDS; this shows what is
    // actually on the portal. A live profile that was never deployed (or last deployed as a
    // takedown) is NOT on the site despite the green chip — the failure that silently took
    // VS Markets offline and left KME Plus500 stale. Only meaningful for live profiles.
    const deployedLive = cell.last_deploy_action === 'deploy';
    let deployMarker: React.ReactNode = null;
    if (cell.status === 'live' && !deployedLive) {
      deployMarker = (
        <Tooltip
          title={
            'Status Live, aber nicht ausgespielt — die Seite ist NICHT auf dem Portal. Öffnen und „Ausspielen" klicken.'
          }
        >
          <CloudOffOutlined sx={{ color: colors.redAccent[400], fontSize: 16 }} />
        </Tooltip>
      );
    } else if (
      cell.status === 'live' &&
      (cell.changed_since_deploy || cell.facts_changed_since_deploy)
    ) {
      // Two different causes, one marker — but the tooltip must name the right one, because
      // "ich habe hier nichts geändert" is exactly the reaction a fact-driven staleness gets.
      deployMarker = (
        <Tooltip
          title={
            cell.changed_since_deploy
              ? 'Änderungen gespeichert, aber noch nicht ausgespielt. „Ausspielen" klicken, damit sie live gehen.'
              : 'Ein zitierter Broker-Fakt wurde geändert — die Seite zeigt noch den alten Wert. „Ausspielen" klicken.'
          }
        >
          <SyncProblemOutlined sx={{ color: colors.blueAccent[300], fontSize: 16 }} />
        </Tooltip>
      );
    }
    return (
      <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
        <Chip
          size="small"
          label={style.label}
          color={style.color}
          variant={cell.has_content ? 'filled' : 'outlined'}
          onClick={() => setEditorProfileId(cell.id)}
          sx={{ cursor: 'pointer' }}
        />
        {deployMarker}
        {cell.needs_review ? (
          <Tooltip title="Import unvollständig — Review nötig">
            <ReportProblemOutlined sx={{ color: colors.redAccent[400], fontSize: 16 }} />
          </Tooltip>
        ) : null}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title="Broker Hub"
        subtitle="Zentrale Verwaltung der Broker-Profile über alle 6 Portale"
      />

      <HelpLegend items={MATRIX_HELP} storageKey="matrix" />

      {error && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.redAccent[700] }}>
          <Typography>{error}</Typography>
        </Paper>
      )}

      <Box display="flex" gap={1} mb={2} alignItems="center" flexWrap="wrap">
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddCircleOutline />}
          onClick={() => setNewProfileOpen(true)}
        >
          Neues Profil
        </Button>
        <Button
          variant="outlined"
          startIcon={<BrushOutlined />}
          onClick={() => setDesignOpen(true)}
        >
          Design (Basis-CSS)
        </Button>
        <Button
          variant="outlined"
          startIcon={<ArticleOutlined />}
          onClick={() => setOverviewTextOpen(true)}
          sx={{ ml: 1 }}
        >
          Übersichtstexte
        </Button>
        <Button
          variant="outlined"
          startIcon={<GavelOutlined />}
          onClick={() => setComplianceOpen(true)}
        >
          Compliance
        </Button>
        <Button
          variant="outlined"
          startIcon={<FactCheckOutlined />}
          onClick={() => setFactsOpen(true)}
        >
          Broker-Fakten
        </Button>
        <Box flexGrow={1} />
        <Chip label={`${matrix?.brokers.length ?? 0} Broker`} />
        <Chip label={`${matrix?.sites.length ?? 0} Portale`} />
        <Chip
          icon={discrepancies.length ? <ErrorOutline /> : <CheckCircleOutline />}
          label={`${discrepancies.length} offene Diskrepanzen`}
          color={discrepancies.length ? 'warning' : 'success'}
          variant="outlined"
        />
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Matrix" />
        <Tab label={`Diskrepanzen (${discrepancies.length})`} />
        <Tab label={`Vergleichsseiten (${comparePages.length})`} />
        <Tab label="Schnipsel" />
        <Tab label="Broker-Cloud" />
        <Tab label="Anzeigen" />
      </Tabs>

      {/*
        Schnipsel liegen als TAB hier und nicht als eigene Seite: `/broker-hub` ist in `App.tsx`,
        in der Sidebar und in `groupAuth.apiPrefixes` bereits registriert. Eine eigene Seite hätte
        dieselbe Registrierung ein zweites Mal gebraucht — und „alle drei oder es ist kaputt" ist
        die Stelle, an der genau das schiefgeht.
      */}
      {tab === 3 && <SnippetsTab sites={matrix?.sites ?? []} brokers={matrix?.brokers ?? []} />}
      {/* Broker-Cloud (Footer-Logo-Leiste aller Portale) aus demselben Grund als Tab. */}
      {tab === 4 && <BrokerCloudTab />}
      {/* Broker-Anzeigen live auf finanztrends.de (Spec 2026-10-20 § 4): Not-Aus, Gewichte, Statistik. */}
      {tab === 5 && <AdsLiveTab />}

      {tab === 0 && matrix && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Broker</TableCell>
                {matrix.sites.map((s) => (
                  <TableCell key={s.site_key} align="center">
                    <Tooltip title={s.base_url}>
                      <span>{s.name}</span>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {matrix.brokers.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">{b.name}</Typography>
                      <Typography variant="caption" color={colors.grey[400]}>
                        {b.slug}
                      </Typography>
                      {/*
                        ⚠ Der Zustand steht NEBEN dem Namen, nicht hinter einem Klick. Ein halb
                        ausgerollter Broker ist der teure Fall — drei Portale mit 81 %, drei mit
                        80 % —, und er sieht ohne diese Anzeige aus wie ein fertiger.
                        Ist kein Zustand geladen, wird auch nichts behauptet.
                      */}
                      {rollout[b.id] && !rollout[b.id].vollstaendig && (
                        <Tooltip
                          title={rollout[b.id].portale
                            .filter((p) => p.zustand !== 'kein_profil' && p.zustand !== 'aktuell')
                            .map((p) => `${p.siteKey}: ${ROLLOUT_TEXTE[p.zustand].text}`)
                            .join(' · ')}
                        >
                          <Chip
                            size="small"
                            color="warning"
                            label={`${
                              rollout[b.id].portale.filter(
                                (p) => p.zustand !== 'kein_profil' && p.zustand !== 'aktuell',
                              ).length
                            } Portal(e) offen`}
                          />
                        </Tooltip>
                      )}
                      <Button size="small" onClick={() => setBearbeiteBroker(b)}>
                        Bearbeiten
                      </Button>
                      <Button size="small" onClick={() => setRolloutFuer(b)}>
                        Ausspielen
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setAffiliateFuer({ id: b.id, name: b.name })}
                      >
                        Affiliate-Links
                      </Button>
                    </Box>
                  </TableCell>
                  {matrix.sites.map((s) => (
                    <TableCell key={s.site_key} align="center">
                      {renderCell(b, s.site_key)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Broker</TableCell>
                <TableCell>Portal</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Beschreibung</TableCell>
                <TableCell align="right">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {discrepancies.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>{d.broker_slug}</TableCell>
                  <TableCell>{d.site_key ?? '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={d.kind} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={d.evidence ?? ''}>
                      <span>{d.description}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => resolveDiscrepancy(d.id, 'resolved')}>
                      Erledigt
                    </Button>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => resolveDiscrepancy(d.id, 'dismissed')}
                    >
                      Ignorieren
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!discrepancies.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography p={1}>Keine offenen Diskrepanzen 🎉</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && (
        <>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button size="small" variant="outlined" onClick={() => setNewComparePageOpen(true)}>
              Neue Vergleichsseite
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Seite</TableCell>
                  <TableCell>Portal</TableCell>
                  <TableCell align="center">Broker</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Hinweis</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparePages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color={colors.grey[400]}>
                        Noch keine Vergleichsseite angelegt.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {comparePages.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Button size="small" onClick={() => setEditorProfileId(p.id)}>
                        {p.page_title || p.page_slug}
                      </Button>
                      <Typography variant="caption" color={colors.grey[400]} display="block">
                        {p.page_slug}
                      </Typography>
                    </TableCell>
                    <TableCell>{p.site_key}</TableCell>
                    <TableCell align="center">{Number(p.broker_count ?? 0)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={p.status}
                        color={p.status === 'live' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {/* Zwei verschiedene Gründe, neu zu deployen — und der zweite ist der, den
                          es ohne die abgeleitete Broker-Tabelle gar nicht gäbe: zentrale Daten
                          haben sich geändert, ohne dass jemand die Seite angefasst hat. */}
                      {Number(p.changed_since_deploy) === 1 && (
                        <Chip size="small" color="warning" variant="outlined" label="bearbeitet" />
                      )}
                      {Number(p.broker_data_changed_since_deploy) === 1 && (
                        <Tooltip title="Note, Logo oder Risikohinweis eines gezeigten Brokers wurde zentral geändert — die Seite zeigt bis zum nächsten Deploy den alten Stand.">
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Broker-Daten geändert"
                            sx={{ ml: 0.5 }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <NewComparePageDialog
        open={newComparePageOpen}
        sites={matrix?.sites ?? []}
        pages={comparePages}
        onClose={() => setNewComparePageOpen(false)}
        onCreated={(p) => {
          setNewComparePageOpen(false);
          load();
          // Direkt in den Editor: eine frisch angelegte Vergleichsseite ist leer, und der einzige
          // sinnvolle nächste Schritt ist, Bausteine hineinzusetzen. Bei einer KOPIE gilt das
          // genauso — dort zeigt der Editor beim Öffnen zusätzlich die Sperrgründe des
          // Zielportals („Diese Seite kann noch nicht live gehen"), was den früher geplanten
          // Vorlauf ersetzt.
          //
          // ⚠ Die konkrete Variantenliste aus `p.warnungen` wird hier NICHT angezeigt — der
          // Dialog hat vor dem Anlegen bereits auf das unveränderte Design und die Links
          // hingewiesen. Benannte Lücke, kein Versehen: ein zweiter Anzeigeweg für dieselbe
          // Aussage wäre mehr Apparat als Nutzen.
          setEditorProfileId(Number(p.id));
        }}
      />

      <ProfileEditorDialog
        profileId={editorProfileId}
        onClose={() => setEditorProfileId(null)}
        onChanged={load}
        onAffiliateLinks={(id, name) => setAffiliateFuer({ id, name })}
        affiliateStand={affiliateStand}
      />
      <NewProfileDialog
        open={newProfileOpen}
        sites={matrix?.sites ?? []}
        brokers={matrix?.brokers ?? []}
        onClose={() => setNewProfileOpen(false)}
        onCreated={onProfileCreated}
      />
      {/* Mit `&&` gerendert, damit der Dialog beim Schliessen ABMONTIERT — sonst ueberlebt der
          Entwurf des vorigen Brokers und der naechste Aufruf zeigt dessen Werte
          (.claude/rules/react-frontend.md). */}
      {bearbeiteBroker && (
        <BrokerEditDialog
          broker={bearbeiteBroker}
          onSchliessen={() => setBearbeiteBroker(null)}
          // Die Broker-ZEILE kommt aus `getMatrix`, nicht aus `listBrokers` — es genuegt also
          // nicht, die Brokerliste neu zu holen; die ganze Matrix muss neu geladen werden.
          onGespeichert={load}
        />
      )}

      <AffiliateLinksDialog
        brokerId={affiliateFuer?.id ?? null}
        brokerName={affiliateFuer?.name ?? ''}
        onSchliessen={() => setAffiliateFuer(null)}
        onGeaendert={() => {
          setAffiliateStand((n) => n + 1);
          void load();
        }}
      />
      <BrokerRolloutDialog
        brokerId={rolloutFuer?.id ?? null}
        brokerName={rolloutFuer?.name ?? ''}
        onSchliessen={() => setRolloutFuer(null)}
        onFertig={() => void load()}
      />
      <DesignDialog
        open={designOpen}
        sites={matrix?.sites ?? []}
        onClose={() => setDesignOpen(false)}
      />
      <OverviewTextDialog
        open={overviewTextOpen}
        sites={matrix?.sites ?? []}
        onClose={() => setOverviewTextOpen(false)}
      />
      <ComplianceDialog
        open={complianceOpen}
        brokers={matrix?.brokers ?? []}
        sites={matrix?.sites ?? []}
        onClose={() => setComplianceOpen(false)}
      />
      <FactsDialog
        open={factsOpen}
        brokers={matrix?.brokers ?? []}
        sites={matrix?.sites ?? []}
        onClose={() => setFactsOpen(false)}
      />
    </Box>
  );
};

export default BrokerHub;
